import { CodeBlock, Col, Table, Text, type TextStyle, useTheme } from "@rally/ui-kit";
import type { ReactNode } from "react";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { type Candidate, runsOf } from "../composer/mentions.ts";
import { type Block, blocksOf, type Item, isMarked, type Span } from "./markdown.ts";
import { Tagged } from "./tagged.tsx";
import type { Taggable } from "./tags.ts";

const CODE_SIZE = 13;
const CODE_LINE = 19;

const MARKER_WIDTH = 20;
const A_STEP_IN = 14;

function Words({ text, who, style }: { text: string; who: readonly Candidate[]; style?: TextStyle }) {
  return (
    <>
      {runsOf(text, who).map((run) => (
        <Text
          key={run.at}
          typeName="body"
          color={run.mentions ? "accent" : "inherit"}
          style={style}
          accessibilityLabel={
            run.mentions ? say("mention.names")(calledBy(run.mentions) ?? run.mentions.handle) : undefined
          }
          testID={run.mentions ? `mention-in-line-${run.mentions.handle}` : undefined}
        >
          {run.text}
        </Text>
      ))}
    </>
  );
}

function Link({ shown, href, host }: { shown: string; href: string; host: string }) {
  const says = shown.trim() === href || shown.trim() === host;
  return (
    <>
      <Text
        typeName="body"
        color="accent"
        style={{ textDecorationLine: "underline" }}
        accessibilityRole="link"
        accessibilityLabel={say("said.linkTo")(shown, host)}
        testID={`said-link-${host}`}
        onPress={() => {
          globalThis.open?.(href, "_blank", "noopener,noreferrer");
        }}
      >
        {shown}
      </Text>
      {says ? null : (
        <Text typeName="caption" color="text.tertiary" testID={`said-link-host-${host}`}>
          {` (${host})`}
        </Text>
      )}
    </>
  );
}

function Runs({
  spans,
  who,
  style,
}: {
  spans: readonly Span[];
  who: readonly Candidate[];
  style?: TextStyle;
}) {
  const theme = useTheme();
  const nodes: ReactNode[] = [];
  spans.forEach((span, index) => {
    const key = `${index}-${span.kind}`;
    if (span.kind === "link") {
      nodes.push(<Link key={key} shown={span.text} href={span.href} host={span.host} />);
      return;
    }
    if (span.kind === "code") {
      nodes.push(
        <Text
          key={key}
          typeName="body"
          color="text.primary"
          style={{
            fontFamily: theme.font.mono,
            fontSize: CODE_SIZE,
            lineHeight: CODE_LINE,
            backgroundColor: theme.color["bg.surfaceAlt"],
          }}
        >
          {` ${span.text} `}
        </Text>,
      );
      return;
    }
    const weight: TextStyle | undefined =
      span.kind === "strong"
        ? { fontWeight: "600" }
        : span.kind === "em"
          ? { fontStyle: "italic" }
          : undefined;
    nodes.push(<Words key={key} text={span.text} who={who} style={{ ...style, ...weight }} />);
  });
  return <>{nodes}</>;
}

function Listed({ items, who }: { items: readonly Item[]; who: readonly Candidate[] }) {
  return (
    <Col style={{ gap: 2 }}>
      {items.map((item) => (
        <Col key={item.at} style={{ flexDirection: "row", paddingLeft: item.depth * A_STEP_IN }}>
          <Text typeName="body" color="text.tertiary" style={{ width: MARKER_WIDTH }}>
            {item.marker}
          </Text>
          <Text typeName="body" color="text.secondary" style={{ flex: 1 }}>
            <Runs spans={item.spans} who={who} />
          </Text>
        </Col>
      ))}
    </Col>
  );
}

function Drawn({ block, who }: { block: Block; who: readonly Candidate[] }) {
  const theme = useTheme();
  if (block.kind === "code")
    return (
      <Col testID="said-code" style={{ alignSelf: "stretch", maxWidth: "100%" }}>
        <CodeBlock code={block.text} language={block.language} />
      </Col>
    );
  if (block.kind === "table")
    return (
      <Table
        headers={block.headers.map((_, index) => String(index))}
        labels={block.headers.map((spans) => (
          <Runs key="label" spans={spans} who={who} style={{ fontWeight: "600" }} />
        ))}
        align={block.align}
        rows={block.rows.map((row) =>
          Object.fromEntries(
            row.map((spans, index) => [
              String(index),
              <Text
                key="value"
                typeName="body"
                color="text.secondary"
                style={{ textAlign: block.align[index] }}
              >
                <Runs spans={spans} who={who} />
              </Text>,
            ]),
          ),
        )}
      />
    );
  if (block.kind === "list") return <Listed items={block.items} who={who} />;
  if (block.kind === "quote") {
    return (
      <Col style={{ flexDirection: "row", gap: theme.space[2] }} testID="said-quote">
        <Col style={{ width: 2, borderRadius: 1, backgroundColor: theme.color["border.strong"] }} />
        <Text typeName="body" color="text.tertiary" style={{ flex: 1, fontStyle: "italic" }}>
          <Runs spans={block.spans} who={who} />
        </Text>
      </Col>
    );
  }

  return (
    <Text
      typeName={block.kind === "strong" ? "bodySemibold" : "body"}
      color={block.kind === "strong" ? "text.primary" : "text.secondary"}
    >
      <Runs
        spans={block.spans}
        who={who}
        style={block.kind === "strong" ? { fontWeight: "600" } : undefined}
      />
    </Text>
  );
}

export function Marked({
  text,
  who,
  files = [],
  conversation,
  me,
}: {
  text: string;
  who: readonly Candidate[];

  files?: readonly Taggable[];

  conversation?: string;
  me?: string;
}) {
  const theme = useTheme();

  if (!isMarked(text))
    return <Tagged text={text} who={who} files={files} conversation={conversation} me={me} />;
  const blocks = blocksOf(text);
  return (
    <Col style={{ gap: theme.space[2], maxWidth: "100%" }} testID="said-marked">
      {blocks.map((block) => (
        <Drawn key={block.at} block={block} who={who} />
      ))}
    </Col>
  );
}
